// The customer page's prompt logging (REQ-244): whether the org logs prompts and whether this
// session may read them, the scope the library's Prompt logs sub-tab reads (`promptLogsVisible`).
// The proxy has the same pair from `/api/me`; here it is built from the org's settings
// (`GET /v1/orgs/{orgId}/settings` `promptLoggingEnabled`) and `holdsPromptReader`, this shell's
// one prompt-reader answer. The library decides presence from the pair alone.
import { useCallback, useEffect, useState } from "preact/hooks";
import type { ApiClient, PromptLogging, PromptLoggingState } from "@nexus/ui";
import type { Session } from "../../session/session";

function ownOrg(session: Session, orgId: string): boolean {
  return session.me.orgId === null || session.me.orgId === orgId;
}

/**
 * Whether the API will answer: `prompts:read` as `GET /v1/me` reports it, the `prompt-reader`
 * flag on an org admin's own user row in this org, or the super-admin rank, which holds it for
 * every org (REQ-016; decision of 2026-10-03). An org admin's role never implies it. This reads
 * the list the API sent, not `Session.can`, so it fails closed against an API that has not
 * granted the rank the permission.
 */
export function holdsPromptReader(session: Session, orgId: string): boolean {
  return ownOrg(session, orgId) && (session.me.permissions ?? []).includes("prompts:read");
}

/** The pair from the org's setting and the session. */
export function promptLoggingOf(session: Session, orgId: string, promptLoggingEnabled: boolean): PromptLogging {
  return { enabled: promptLoggingEnabled, canRead: holdsPromptReader(session, orgId) };
}

type Answer = { orgId: string; enabled: boolean } | { orgId: string; failed: true };

export interface PromptLoggingRead {
  /** "loading" until the settings answer (and after the org changes), "failed" when the read fails. */
  state: PromptLoggingState;
  /** Reads the settings again. */
  retry(): void;
}

/**
 * The org's prompt logging for this session. Neither "loading" nor "failed" is a pair, so
 * `promptLogsVisible` keeps the sub-tab hidden until the setting is known (fail closed), and
 * neither reads as "logging is off".
 */
export function usePromptLogging(client: ApiClient, session: Session, orgId: string): PromptLoggingRead {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setAnswer(null);
    let current = true;
    client
      .get("/v1/orgs/{orgId}/settings", { path: { orgId } })
      .then((settings) => current && setAnswer({ orgId, enabled: settings.promptLoggingEnabled }))
      .catch(() => current && setAnswer({ orgId, failed: true }));
    return () => {
      current = false;
    };
  }, [client, orgId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const state: PromptLoggingState = answer?.orgId !== orgId ? "loading" : "failed" in answer ? "failed" : promptLoggingOf(session, orgId, answer.enabled);
  return { state, retry };
}
